import { ScenePreview } from "@cva/renderer";
import {
  type Asset,
  createMockupDocument,
  DEVICE_CATEGORIES,
  DEVICE_CATEGORY_LABELS,
  DEVICE_MODELS,
  deviceCanRotate,
  deviceForScreenshot,
  deviceModel,
  devicePerspectives,
  MOCKUP_BACKGROUNDS,
  MOCKUP_FORMATS,
  type MockupBackground,
  type MockupFormat,
  paintToCss,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { orpc } from "../lib/api";
import { cn, errorMessage } from "../lib/utils";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";
import { Field, Input, Select } from "./ui/input";

type MockupTarget = "media" | "editor";

/**
 * "Mockup erstellen" (docs/19-mockups.md): a screenshot from the media library
 * in a device. Either as a new image in the media library – the device alone,
 * transparent and cropped to it, rendered by the server like a cut-out – or
 * as a new design with background and page format, fine-tuning in the editor.
 */
export function MockupDialog({
  orgId,
  asset,
  defaultProjectId,
  onSaved,
  onClose,
}: {
  orgId: string;
  asset: Asset;
  defaultProjectId?: string | null | undefined;
  /** Offers "Als Bild" (media library); gets the new image (still being rendered). */
  onSaved?: ((image: Asset) => void) | undefined;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const suggested = deviceForScreenshot(asset);
  const [target, setTarget] = useState<MockupTarget>(onSaved ? "media" : "editor");
  const toMedia = target === "media";
  const [device, setDevice] = useState(suggested.device);
  const [view, setView] = useState("hero");
  const [background, setBackground] = useState<MockupBackground>("soft");
  const [format, setFormat] = useState<MockupFormat>("square");
  const [title, setTitle] = useState(`Mockup ${asset.name}`);
  const projects = useQuery(
    orpc.projects.list.queryOptions({ input: { orgId, includeArchived: false } }),
  );
  const [projectId, setProjectId] = useState(defaultProjectId ?? "");
  useEffect(() => {
    if (!projectId && projects.data?.[0]) setProjectId(projects.data[0].id);
  }, [projectId, projects.data]);

  const model = deviceModel(device);
  const perspectives = devicePerspectives(device);
  const tilt = (perspectives.find((entry) => entry.id === view) ?? perspectives[0]!).tilt;
  const landscape =
    deviceCanRotate(model) && asset.width && asset.height
      ? asset.width > asset.height !== model.screen.width > model.screen.height
      : false;
  // The preview shows what gets saved: in the media library only the device.
  const content = useMemo(
    () =>
      createMockupDocument({
        device: { device, landscape, tilt },
        screenshot: asset,
        format,
        background,
        bare: toMedia,
      }),
    [device, landscape, tilt, asset, format, background, toMedia],
  );
  const resolveAsset = (id: string) =>
    id === asset.id ? { url: asset.url, width: asset.width, height: asset.height } : undefined;

  const create = useMutation(orpc.documents.create.mutationOptions());
  const createProject = useMutation(orpc.projects.create.mutationOptions());
  const saveImage = useMutation(orpc.media.createMockup.mutationOptions());
  const noProjects = projects.data?.length === 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (toMedia) {
      try {
        const image = await saveImage.mutateAsync({
          orgId,
          assetId: asset.id,
          device,
          landscape,
          tilt,
        });
        // Appears in the media library right away ("wird erstellt"), like a cut-out.
        void queryClient.invalidateQueries({ queryKey: orpc.media.list.key() });
        toast.success("Mockup wird erstellt – es landet gleich in der Mediathek.");
        onClose();
        onSaved?.(image);
      } catch (error) {
        toast.error(errorMessage(error));
      }
      return;
    }
    try {
      const target = noProjects
        ? (await createProject.mutateAsync({ orgId, name: "Mockups" })).id
        : projectId;
      if (!target) return;
      const document = await create.mutateAsync({
        orgId,
        projectId: target,
        content,
        title: title.trim() || "Mockup",
      });
      await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
      onClose();
      await navigate({
        to: "/o/$orgId/documents/$documentId",
        params: { orgId, documentId: document.id },
      });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  // Tall devices alone (a phone) would otherwise make the dialog very high.
  const previewBox = toMedia ? 320 : 360;
  const previewWidth =
    content.format.width >= content.format.height
      ? previewBox
      : previewBox * (content.format.width / content.format.height);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title="Mockup erstellen"
        description={
          toMedia
            ? "Der Screenshot kommt in ein Gerät und wird als Bild ohne Hintergrund in der Mediathek gespeichert."
            : "Der Screenshot kommt in ein Gerät – alles Weitere passt du danach im Editor an."
        }
        className="max-h-[calc(100dvh-2rem)] max-w-3xl overflow-y-auto"
      >
        <form onSubmit={submit} className="grid gap-6 md:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="flex min-h-80 items-center justify-center rounded-lg bg-[repeating-conic-gradient(#f4f4f5_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] p-4">
            <div
              className={cn(!toMedia && "overflow-hidden rounded-md shadow-sm ring-1 ring-black/5")}
            >
              <ScenePreview document={content} width={previewWidth} resolveAsset={resolveAsset} />
            </div>
          </div>
          <div className="space-y-3.5">
            {onSaved ? (
              <Field label="Speichern als">
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-zinc-100 p-1">
                  {(
                    [
                      ["media", "Als Bild"],
                      ["editor", "Als Design"],
                    ] as const
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      aria-pressed={target === id}
                      onClick={() => setTarget(id)}
                      className={cn(
                        "h-8 rounded-md px-2 text-xs font-medium",
                        target === id
                          ? "bg-white text-zinc-900 shadow-sm"
                          : "text-zinc-600 hover:text-zinc-900",
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Field>
            ) : null}
            <Field label="Gerät">
              <Select value={device} onChange={(event) => setDevice(event.target.value)}>
                {DEVICE_CATEGORIES.map((category) => (
                  <optgroup key={category} label={DEVICE_CATEGORY_LABELS[category]}>
                    {DEVICE_MODELS.filter((entry) => entry.category === category).map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name}
                        {entry.id === suggested.device ? " (passt)" : ""}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            </Field>
            <Field label="Ansicht">
              <div className="flex flex-wrap gap-1">
                {perspectives.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setView(entry.id)}
                    className={cn(
                      "h-7 rounded-full border px-2.5 text-xs",
                      (perspectives.some((p) => p.id === view) ? view : "front") === entry.id
                        ? "border-brand-500 bg-brand-50 text-brand-700"
                        : "border-zinc-200 text-zinc-600 hover:bg-zinc-50",
                    )}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>
            </Field>
            {toMedia ? null : (
              <>
                <Field label="Hintergrund">
                  <div className="flex gap-1.5">
                    {MOCKUP_BACKGROUNDS.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        title={entry.label}
                        aria-label={entry.label}
                        aria-pressed={background === entry.id}
                        onClick={() => setBackground(entry.id)}
                        className={cn(
                          "size-8 rounded-full border border-black/10 ring-offset-2",
                          background === entry.id
                            ? "ring-2 ring-brand-500"
                            : "hover:ring-2 hover:ring-zinc-300",
                          entry.id === "transparent" &&
                            "bg-[repeating-conic-gradient(#e4e4e7_0%_25%,#fff_0%_50%)] bg-[length:8px_8px]",
                        )}
                        style={
                          entry.id === "transparent"
                            ? undefined
                            : { background: paintToCss(entry.paint) }
                        }
                      />
                    ))}
                  </div>
                </Field>
                <Field label="Format">
                  <Select
                    value={format}
                    onChange={(event) => setFormat(event.target.value as MockupFormat)}
                  >
                    {MOCKUP_FORMATS.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Titel">
                  <Input value={title} onChange={(event) => setTitle(event.target.value)} />
                </Field>
                {noProjects ? null : (
                  <Field label="Projekt">
                    <Select
                      value={projectId}
                      onChange={(event) => setProjectId(event.target.value)}
                    >
                      {projects.data?.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
                {background === "transparent" ? (
                  <p className="text-xs text-zinc-500">
                    Tipp: Beim PNG-Export „Transparenter Hintergrund“ wählen.
                  </p>
                ) : null}
              </>
            )}
          </div>
          <div className="md:col-span-2">
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>
                Abbrechen
              </Button>
              <Button
                type="submit"
                loading={create.isPending || createProject.isPending || saveImage.isPending}
              >
                {toMedia ? "In der Mediathek speichern" : "Im Editor öffnen"}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
